import { PROMO_CODE_PARAM, normalizePromoCode } from '../../shared/types';

/**
 * Transport d'un code promo entre le scan du QR et l'échange effectif.
 *
 * Le chemin n'est pas direct : le visiteur arrive sur `?c=EXEMPLE10`, saisit son
 * email, reçoit un code à 6 chiffres, le tape — et ce n'est qu'APRÈS que la
 * session existe et que l'échange devient possible. Le code doit survivre à
 * cette traversée, y compris si l'onglet est rechargé entre-temps.
 *
 * Deux porteurs redondants, volontairement :
 *   1. localStorage (ici) — couvre le parcours normal, onglet unique ;
 *   2. `user_metadata.promo_code`, posé à l'inscription (cf. AuthContext) —
 *      couvre le cas où la session s'ouvre AILLEURS : lien magique ouvert dans
 *      le navigateur in-app de Gmail, ou sur un autre appareil. Là, le
 *      localStorage de l'onglet d'origine est hors de portée.
 *
 * Aucun des deux n'est une source d'autorité : la validité du code, son
 * plafond et l'unicité de l'échange sont vérifiés côté serveur par la RPC.
 * Ces porteurs ne font que transmettre une intention.
 */

const STORAGE_KEY = 'evinly.promo.pending';

/** Lit le code dans l'URL courante (`?c=…`, ou `?code=…` en tolérance). */
export function readPromoFromUrl(): string {
  try {
    const params = new URLSearchParams(window.location.search);
    return normalizePromoCode(params.get(PROMO_CODE_PARAM) ?? params.get('code'));
  } catch {
    return '';
  }
}

/**
 * Retire le paramètre de l'URL sans recharger la page.
 *
 * Sinon le code reste dans la barre d'adresse et part avec le premier partage
 * de lien ou le premier ajout aux favoris — et l'utilisateur revoit l'écran
 * « offre » à chaque visite, longtemps après l'avoir déjà encaissée.
 */
export function stripPromoFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(PROMO_CODE_PARAM) && !url.searchParams.has('code')) return;
    url.searchParams.delete(PROMO_CODE_PARAM);
    url.searchParams.delete('code');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  } catch {
    /* navigation privée / URL exotique : sans gravité, le code reste affiché */
  }
}

/** Le code en attente d'échange, '' si aucun. */
export function getPendingPromo(): string {
  try {
    return normalizePromoCode(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    // Safari en navigation privée peut lever à la simple lecture.
    return '';
  }
}

export function setPendingPromo(code: string): void {
  const normalized = normalizePromoCode(code);
  if (!normalized) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, normalized);
  } catch {
    /* le porteur `user_metadata` prend le relais */
  }
}

/**
 * Récupère le code de l'URL, le met en attente et nettoie la barre d'adresse.
 * Renvoie le code en attente (celui qui vient d'être lu, ou celui déjà stocké).
 *
 * Idempotent et sans ordre imposé : l'écran de connexion comme le hook
 * d'échange l'appellent tous les deux au montage. Le premier à passer capture
 * l'URL, le second retrouve la même valeur en mémoire — aucun des deux n'a
 * besoin de savoir lequel s'est exécuté en premier.
 */
export function capturePromoFromUrl(): string {
  const fromUrl = readPromoFromUrl();
  if (fromUrl) {
    setPendingPromo(fromUrl);
    stripPromoFromUrl();
    return fromUrl;
  }
  return getPendingPromo();
}

export function clearPendingPromo(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* rien à nettoyer si l'écriture avait déjà échoué */
  }
}
